/**
 * @fileoverview A program viewer's Profile: Hans itself, their account, the
 * Academy's tutorials for viewers and support. It takes the place Hans
 * Academy holds in an administrator's rail.
 *
 * - **About Hans** comes first: a viewer is a school coordinator or a sponsor
 *   who met Hans through someone else's program, so this is where they learn
 *   what it is and can pass it on. Sharing sends a link to Hans for
 *   organizations, by the device's share sheet where there is one, by email,
 *   or copied.
 * - **Your account**: who is signed in, and changing the password
 *   (`POST /org/auth/change-password`, which a viewer may call).
 * - **Appearance**: light or dark, saved on this device, as every portal has.
 * - **Hans Academy for program viewers**: the viewer guide's sections, each
 *   opening in a new tab (a public page).
 * - **Help**: support for Hans itself. Questions about a program go to the
 *   organization, from that program's Settings tab.
 */

import { useContext, useState } from "react";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import { Button, ButtonLink, Card, Icon, PageShell } from "@/components/studio/primitives";
import { SettingsRow, SettingsSection } from "@/components/settings/SettingsSection";
import ThemeToggle from "@/components/ThemeToggle";
import OrgChangePassword from "@/components/org/OrgChangePassword";
import { adminIdentity, orgIdentity } from "@/components/org/OrgLayout";
import { companySiteLabel, supportEmail } from "@/lib/companyBrand";

/** Where sharing Hans sends someone: Hans for organizations, on the public site. */
export const SHARE_URL = `https://${companySiteLabel()}/for-organizations`;

/** What Hans is, in About Hans and in the message sharing it sends. */
const ABOUT_HANS = "Hans is an end-to-end lesson management platform for Organizations, Instructors, and Students.";

/** The viewer guide's sections, in the order a new viewer meets them. */
export const VIEWER_TUTORIALS = [
  { id: "around", title: "Find your way around", desc: "Your programs, the tiles at the top and what each tab holds." },
  { id: "lessons", title: "Lessons and attendance", desc: "Sort and export the sessions, open a lesson's notes, read each class's attendance." },
  { id: "roster", title: "Upload a class roster", desc: "Paste a list, check the preview, and add the students to a class." },
  { id: "settings", title: "Your emails and settings", desc: "Choose your emails, reach the program's contact, or leave a program." },
];

function ShareHans({ orgName }) {
  const [copied, setCopied] = useState(false);
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const subject = "Hans, for music programs";
  const body = `I've been following a music program through Hans${orgName ? ` (${orgName} runs it)` : ""}. ${ABOUT_HANS} Have a look: ${SHARE_URL}`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link");
    }
  };

  const share = async () => {
    try {
      await navigator.share({ title: subject, text: body, url: SHARE_URL });
    } catch {
      /* closed without sharing */
    }
  };

  return (
    <div className="mt-4 rounded-[14px] border border-[var(--studio-divider)] bg-[var(--studio-base)] p-4" data-testid="viewer-share-panel">
      <p className="font-sans text-[14px] text-[var(--text-secondary)]">
        Know a teacher, a school or a music program that could use Hans? Send them this link.
      </p>
      <div className="mt-3 flex flex-col gap-2 sm:flex-row sm:items-center">
        <input
          readOnly
          value={SHARE_URL}
          onFocus={(e) => e.target.select()}
          aria-label="Link to Hans"
          data-testid="viewer-share-url"
          className="min-w-0 flex-1 rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-3 py-2 font-mono text-[13px] text-[var(--text-primary)] outline-none"
        />
        <div className="flex flex-wrap gap-2">
          <Button size="sm" icon={copied ? "check" : "copy"} onPress={copy} data-testid="viewer-share-copy">
            {copied ? "Copied" : "Copy link"}
          </Button>
          <ButtonLink
            href={`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
            icon="mail"
            data-testid="viewer-share-email"
          >
            Email it
          </ButtonLink>
          {canShare && (
            <Button size="sm" variant="ghost" icon="share" onPress={share} data-testid="viewer-share-native">
              More…
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default function OrgViewerProfilePage() {
  const { org, admin } = useContext(OrgAuthContext) || {};
  const { name: orgName } = orgIdentity(org);
  const [sharing, setSharing] = useState(false);
  const [changing, setChanging] = useState(false);
  const name = [admin?.first_name, admin?.last_name].filter(Boolean).join(" ") || adminIdentity(admin) || "—";

  return (
    <PageShell width="reading" data-testid="viewer-profile">
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Profile</h1>

      {/* Hans first: how a viewer learns what it is, and passes it on. */}
      <Card className="mb-6" data-testid="viewer-about-hans">
        <div className="flex items-start gap-4">
          <img src="/logo192.png" alt="" className="h-11 w-11 shrink-0 rounded-[12px]" />
          <div className="min-w-0 flex-1">
            <h2 className="font-sans text-lg font-semibold tracking-[-0.01em] text-[var(--text-primary)]">About Hans</h2>
            <p className="mt-1 font-sans text-[14px] leading-relaxed text-[var(--text-secondary)]">{ABOUT_HANS}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              <ButtonLink href="/for-organizations" target="_blank" rel="noreferrer" iconRight="external" data-testid="viewer-learn-more">
                Learn more about Hans
              </ButtonLink>
              <Button
                size="sm"
                variant={sharing ? "ghost" : "primary"}
                icon="share"
                onPress={() => setSharing((v) => !v)}
                data-testid="viewer-share-hans"
              >
                Share Hans
              </Button>
            </div>
          </div>
        </div>
        {sharing && <ShareHans orgName={org ? orgName : null} />}
      </Card>

      <div className="flex flex-col gap-6">
        <SettingsSection title="Your account" testId="viewer-account">
          <SettingsRow title="Name" trailing={<span className="font-sans text-[14px] text-[var(--text-secondary)]">{name}</span>} />
          <SettingsRow title="Email" trailing={<span className="font-sans text-[14px] text-[var(--text-secondary)]">{admin?.email || "—"}</span>} />
          <SettingsRow
            last
            title="Password"
            desc="Changing it signs you out everywhere else."
            trailing={
              !changing ? (
                <Button size="sm" variant="ghost" icon="lock" onPress={() => setChanging(true)} data-testid="viewer-change-password">
                  Change password
                </Button>
              ) : undefined
            }
          >
            {changing && <OrgChangePassword className="mt-4 flex max-w-sm flex-col gap-4" />}
          </SettingsRow>
        </SettingsSection>

        <SettingsSection title="Appearance" testId="viewer-appearance">
          <SettingsRow last title="Theme" desc="Light is the default. Dark suits a dim room. Saved on this device." trailing={<ThemeToggle />} />
        </SettingsSection>

        <SettingsSection title="Hans Academy for program viewers" testId="viewer-academy">
          {VIEWER_TUTORIALS.map((t, i) => (
            <a
              key={t.id}
              href={`/viewer-guide#${t.id}`}
              target="_blank"
              rel="noreferrer"
              data-testid={`viewer-tutorial-${t.id}`}
              className={`flex items-center gap-4 px-5 py-4 transition-colors hover:bg-[var(--studio-overlay)] md:px-6 ${
                i < VIEWER_TUTORIALS.length - 1 ? "border-b border-[var(--studio-divider)]" : "rounded-b-[inherit]"
              } ${i === 0 ? "rounded-t-[inherit]" : ""}`}
            >
              <Icon name="book" size={18} className="shrink-0 text-[var(--text-tertiary)]" />
              <span className="min-w-0 flex-1">
                <span className="block font-sans text-[15px] font-medium text-[var(--text-primary)]">{t.title}</span>
                <span className="mt-0.5 block font-sans text-[13px] text-[var(--text-tertiary)]">{t.desc}</span>
              </span>
              <Icon name="external" size={15} className="shrink-0 text-[var(--text-tertiary)]" />
            </a>
          ))}
        </SettingsSection>

        <SettingsSection title="Help" testId="viewer-help">
          <SettingsRow
            last
            title="Contact support"
            desc={`For anything about Hans itself. Questions about a program go to ${org ? orgName : "the organization"}, from the program's Settings tab.`}
            trailing={
              <ButtonLink href={`mailto:${supportEmail()}`} icon="mail" data-testid="viewer-support">
                {supportEmail()}
              </ButtonLink>
            }
          />
        </SettingsSection>
      </div>
    </PageShell>
  );
}
